import React, { useEffect, useRef, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { extractValues } from '../../validation/ValidationInput';
import { getLocations } from '../../services/inventoryCorrections.service';
import { deleteOrders, getClients, getOrdersOverview, OrdersOverview } from '../../services/order.service';
import { showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import {
  Button,
  ConfirmDialog,
  DocListRow,
  DocumentTable,
  EmptyState,
  ErrorBanner,
  FilterDef,
  FiltersSheet,
  MenuItem,
  ORDER_STATUS,
  PageFrame,
  Pager,
  PageTitle,
  PERIODS,
  PeriodKey,
  PhoneDocumentList,
  SharePanel,
  STATUS_GROUPS,
  StatusStrip,
  ToolbarCard,
  dayMonth,
  money,
  periodTextOf,
  rangeOf,
  statusOf,
  useLanguage,
} from '../../components/purchasing';
import NewOrderDialog from './NewOrderDialog';

const PAGE_SIZE = 20;
const STATUS_KEYS = STATUS_GROUPS.map((group) => group.key);

interface ListState {
  period: PeriodKey;
  from: string;
  to: string;
  status: string;
  /** '' = all sites. */
  site: string;
  supplier: string;
  search: string;
  page: number;
}

/** The list as the URL left it; the legacy approval and deliveries tabs open their status over all dates. */
const readUrl = (): ListState => {
  const params: any = extractValues(location.search.split('&'));
  const legacy = params.tab === 'approval' ? 'waiting' : params.tab === 'deliveries' ? 'sent' : '';
  const site = params.site === 'all' ? '' : params.site || localStorage.getItem('locationId') || '';
  return {
    period: PERIODS.includes(params.period) ? params.period : legacy ? 'custom' : '30d',
    from: params.from ?? '',
    to: params.to ?? '',
    status: STATUS_KEYS.includes(params.status) ? params.status : legacy || 'all',
    site,
    supplier: params.supplier ?? '',
    search: params.search ?? '',
    page: Math.max(1, Number(params.page) || 1),
  };
};

const writeUrl = (state: ListState) => {
  const params = new URLSearchParams();
  params.set('period', state.period);
  if (state.period === 'custom' && state.from) params.set('from', state.from);
  if (state.period === 'custom' && state.to) params.set('to', state.to);
  if (state.status !== 'all') params.set('status', state.status);
  params.set('site', state.site || 'all');
  if (state.supplier) params.set('supplier', state.supplier);
  if (state.search) params.set('search', state.search);
  if (state.page > 1) params.set('page', String(state.page));
  window.history.replaceState(window.history.state, '', `${location.pathname}?${params.toString()}`);
};

const Order = () => {
  const preLanguage = localStorage.getItem('language');
  const userId = localStorage.getItem('userId') || '';
  const companyId = localStorage.getItem('companyId') || '';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [state, setState] = useState<ListState>(readUrl);
  const [term, setTerm] = useState(state.search.trim());
  const [data, setData] = useState<OrdersOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [sites, setSites] = useState<{ id: string; name: string }[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // /orders/new lands here with the dialog open.
  const [creating, setCreating] = useState(() => /(^|[?&])new=1(&|$)/.test(location.search));
  const [deleting, setDeleting] = useState<DocListRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const requestSeq = useRef(0);

  const update = (patch: Partial<ListState>) => setState((prev) => ({ ...prev, page: 1, ...patch }));

  useEffect(() => writeUrl(state), [state]);
  useDebounceEffect(() => setTerm(state.search.trim()), [state.search], 300);

  // Sites and suppliers for the filters and the New order dialog.
  useEffect(() => {
    const load = () => {
      getLocations(userId, companyId, 0, 0, '')
        .then((response: any) => setSites((response?.data?.data ?? []).map((row: any) => ({ id: String(row.id), name: String(row.name ?? '') }))))
        .catch(() => undefined);
      // Page 0 / limit 0: every supplier, by name.
      getClients(0, 0, '', companyId, 2)
        .then((response: any) => setSuppliers((response?.data?.data ?? []).map((row: any) => ({ id: String(row.id), name: String(row.companyName ?? '') }))))
        .catch(() => undefined);
    };
    load();
    document.addEventListener('newToken', load);
    return () => document.removeEventListener('newToken', load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mine = ++requestSeq.current;
    setLoading(true);
    setFailed(false);
    getOrdersOverview({
      ...rangeOf(state),
      status: state.status === 'all' ? undefined : state.status,
      search: term || undefined,
      supplierId: state.supplier || undefined,
      locationId: state.site || undefined,
      page: state.page,
      limit: PAGE_SIZE,
    })
      .then((response: any) => {
        if (mine === requestSeq.current) setData(response?.data?.data ?? null);
      })
      .catch((error: any) => {
        if (mine !== requestSeq.current) return;
        setFailed(true);
        showApiError(error, { language });
      })
      .finally(() => {
        if (mine === requestSeq.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.period, state.from, state.to, state.status, state.site, state.supplier, state.page, term, reloadKey]);

  useEffect(() => {
    const reload = () => setReloadKey((key) => key + 1);
    document.addEventListener('newToken', reload);
    return () => document.removeEventListener('newToken', reload);
  }, []);

  const siteName = sites.find((site) => site.id === state.site)?.name;
  const periodText = periodTextOf(state, t);

  const totals = data?.totals ?? { orders: 0, amount: 0 };
  const countText = (count: number) => (count === 1 ? t('1 order') : t('{count} orders', { count }));
  const ordersText = countText(totals.orders);
  // The count matches All in the strip; the value leaves cancelled orders out.
  // Phones drop the site: it is one tap away in Filters.
  const meta = [phone ? null : state.site ? siteName : t('All sites'), periodText, data ? countText(Number(data.statusCounts?.all ?? 0)) : null, data ? money(totals.amount) : null]
    .filter(Boolean)
    .join(' · ');

  const counts = data?.statusCounts ?? {};
  const strip = STATUS_GROUPS.filter((group) => group.key !== 'cancelled' || (counts.cancelled ?? 0) > 0 || state.status === 'cancelled').map((group) => ({
    key: group.key,
    label: t(group.label),
    family: group.family,
    count: Number(counts[group.key] ?? 0),
  }));

  const showSite = !state.site;
  const statusIds = new Map((data?.rows ?? []).map((row) => [row.id, row.statusId]));
  const rows: DocListRow[] = (data?.rows ?? []).map((row) => {
    const supplier = row.supplier?.name ?? t('No supplier');
    const site = showSite ? row.location?.name ?? null : null;
    const date = dayMonth(row.createdAt);
    const person = row.createdBy?.name ?? null;
    return {
      id: row.id,
      number: `#${row.internalNumber}`,
      subtitle: row.template?.name ?? null,
      cells: {
        supplier: { main: supplier, sub: site },
        created: { main: date, sub: person },
        total: { main: money(row.total), strong: true },
      },
      status: statusOf(row.statusId),
      phoneText: [supplier, site, row.template?.name].filter(Boolean).join(' · '),
      phoneMeta: [date, person].filter(Boolean).join(' · '),
      phoneAmount: money(row.total),
    };
  });

  const hrefOf = (row: DocListRow) => `/${preLanguage}/orders/${row.id}`;
  const open = (row: DocListRow) => webNavigate(hrefOf(row));
  const menuFor = (row: DocListRow): MenuItem[] => [
    { key: 'open', label: t('Open order'), icon: 'arrowRight', onSelect: () => open(row) },
    ...(statusIds.get(row.id) === ORDER_STATUS.Draft ? [{ key: 'delete', label: t('Delete draft'), icon: 'trash', danger: true, onSelect: () => setDeleting(row) }] : []),
  ];

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deleteOrders(deleting.id);
      showDeleteToast(t('Order Deleted !'));
      setDeleting(null);
      setReloadKey((key) => key + 1);
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setDeleteBusy(false);
    }
  };

  const filters: FilterDef[] = [
    { key: 'site', label: 'Site', icon: 'pin', allLabel: 'All sites', value: state.site, options: sites, onChange: (value) => update({ site: value }) },
    { key: 'supplier', label: 'Supplier', icon: 'truck', allLabel: 'All suppliers', value: state.supplier, options: suppliers, onChange: (value) => update({ supplier: value }) },
  ];

  const newOrder = () => setCreating(true);
  const statusLabel = STATUS_GROUPS.find((group) => group.key === state.status)?.label;
  const empty = term ? (
    <EmptyState icon="search" phone={phone} title={t('No orders match "{search}"', { search: term })} desc={t('Check the number, or search by supplier, template or product.')} />
  ) : state.status !== 'all' && statusLabel ? (
    <EmptyState phone={phone} title={t('No orders with this status')} desc={t('{status} orders in this period show here.', { status: t(statusLabel) })} />
  ) : (
    <EmptyState
      phone={phone}
      title={t('No orders in this period')}
      desc={t('Create an order from a template to send it to a supplier.')}
      action={
        <Button variant="primary" phone={phone} icon="plus" onClick={newOrder}>
          {t('New order')}
        </Button>
      }
    />
  );

  const pager = data ? <Pager phone={phone} t={t} page={state.page} limit={PAGE_SIZE} total={data.count} onPage={(page) => setState((prev) => ({ ...prev, page }))} /> : null;
  const list = phone ? (
    <PhoneDocumentList t={t} label={t('Orders')} rows={rows} loading={loading} hrefOf={hrefOf} onOpen={open} empty={empty} footer={pager} />
  ) : (
    <DocumentTable
      t={t}
      label={t('Orders')}
      numberLabel={t('Order')}
      columns={[
        { key: 'supplier', label: t('Supplier') },
        { key: 'created', label: t('Created'), align: 'right', compact: true },
        { key: 'total', label: t('Total'), align: 'right', compact: true },
      ]}
      rows={rows}
      loading={loading}
      hrefOf={hrefOf}
      onOpen={open}
      menuFor={menuFor}
      menuLabel={(row) => t('Actions for order {number}', { number: row.number })}
      triggerLabel={(row) => t('More actions for order {number}', { number: row.number })}
      empty={empty}
      footer={pager}
    />
  );

  const panel = (
    <SharePanel
      label={t('By supplier')}
      title={t('By supplier')}
      subtitle={data ? t('{period} · {amount} across {orders}', { period: periodText, amount: money(totals.amount), orders: ordersText }) : undefined}
      empty={t('No orders to sum up in this period.')}
      rows={(data?.bySupplier ?? []).map((row) => {
        const share = totals.amount > 0 ? Math.round((Number(row.total) / totals.amount) * 100) : 0;
        return {
          key: row.supplierId,
          name: row.name ?? t('No supplier'),
          value: money(row.total),
          detail: `${row.orders === 1 ? t('1 order') : t('{count} orders', { count: row.orders })} · ${share}%`,
          share,
        };
      })}
    />
  );

  return (
    <PageFrame phone={phone}>
      <PageTitle
        phone={phone}
        t={t}
        title={t('Orders')}
        meta={meta}
        secondary={[{ key: 'templates', label: t('Order templates'), onSelect: () => webNavigate(`/${preLanguage}/orderTemplates`) }]}
        primary={{ key: 'new', label: t('New order'), icon: 'plus', onSelect: newOrder }}
      />
      <ToolbarCard
        phone={phone}
        t={t}
        period={state.period}
        onPeriod={(period) => update({ period })}
        customFrom={state.from}
        customTo={state.to}
        onCustom={(from, to) => update({ from, to })}
        search={state.search}
        onSearch={(search) => setState((prev) => ({ ...prev, search, page: 1 }))}
        searchLabel={t('Search orders')}
        searchPlaceholder={phone ? t('Search orders') : t('Number, supplier or product')}
        filters={filters}
        onOpenFilters={() => setFiltersOpen(true)}
      />
      <StatusStrip phone={phone} label={t('Order status')} items={strip} active={state.status} onPick={(status) => update({ status })} />
      {failed && <ErrorBanner message={t('The orders could not be loaded.')} retryLabel={t('Try again')} onRetry={() => setReloadKey((key) => key + 1)} />}
      {phone ? (
        list
      ) : (
        <div className="pu-split-wide" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 320px)', gap: '24px', alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>{list}</div>
          <div style={{ minWidth: 0 }}>{panel}</div>
        </div>
      )}
      {phone && (
        <FiltersSheet
          open={filtersOpen}
          t={t}
          filters={filters}
          showLabel={data?.count === 1 ? t('Show 1 order') : t('Show {count} orders', { count: data?.count ?? 0 })}
          onClear={() => update({ site: '', supplier: '' })}
          onClose={() => setFiltersOpen(false)}
        />
      )}
      <NewOrderDialog
        open={creating}
        phone={phone}
        t={t}
        language={language}
        sites={sites}
        suppliers={suppliers}
        defaultSiteId={state.site || localStorage.getItem('locationId') || ''}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          webNavigate(`/${preLanguage}/orders/${id}`);
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        phone={phone}
        danger
        busy={deleteBusy}
        title={t('Delete draft order {number}?', { number: deleting?.number ?? '' })}
        body={t('The draft and its products are removed. This cannot be undone.')}
        confirmLabel={t('Delete draft')}
        cancelLabel={t('Keep draft')}
        closeLabel={t('Close')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </PageFrame>
  );
};

export default Order;
